<template>
  <template v-if="ComponetClass && conditionShow">
    <template v-if="config.componentList && config.componentList.length > 0">
      <component
        ref="dataComponent"
        :is="ComponetClass"
        v-bind="{
          ...componentConfig,
          position: calcPosition,
        }"
        v-on="componentEvents"
        :data="storeData"
        :storeData="storeData"
      >
        <component v-for="component in config.componentList" :key="component.id" :is="selfComponent" :config="component" :parentConfig="config" />
      </component>
    </template>
    <template v-else-if="!!config.slot">
      <component
        ref="dataComponent"
        :is="ComponetClass"
        v-bind="{
          ...componentConfig,
          position: calcPosition,
        }"
        v-on="componentEvents"
        :data="storeData"
        :storeData="storeData"
      >
        <template v-for="(_slotConfig, name) in config.slot" #[name]>
          <template v-if="Array.isArray(config.slot[name])">
            <component v-for="component in config.slot[name]" :key="component.id" :is="selfComponent" :config="component" :parentConfig="config" />
          </template>
          <template v-else-if="typeof config.slot[name] === 'string'">
            {{ config.slot[name] }}
          </template>
          <component v-else :is="selfComponent" :key="config.slot[name].id" :config="config.slot[name]" :parentConfig="config" />
        </template>
      </component>
    </template>
    <component
      ref="dataComponent"
      v-else
      :is="ComponetClass"
      v-bind="{
        ...componentConfig,
        position: calcPosition,
      }"
      v-on="componentEvents"
      :data="storeData"
      :storeData="storeData"
    />
  </template>
</template>

<script lang="ts" setup>
import { computed, toRaw, ref, watch, resolveComponent, isRef, inject, onUnmounted } from "vue"
import { calcPropsBindData, calcGroupOffset } from "@p-v/utils/index"
import { debounce } from "lodash-es"

// 为组件命名
const componentName = "ComponentRender"

const getComponent = inject("getComponent") as (component: string, kind: string) => Promise<any>

const pftDataCenter = inject("pftDataCenter") as any

const excuteComponentAction = inject("excuteComponentAction") as any

const PageComponentsRef = inject("PageComponentsRef") as any

const execute = inject("execute") as any

const PAGEEVENTS = inject("PAGEEVENTS") as any

// console.log("getComponent", getComponent)
// console.log("excuteComponentAction", excuteComponentAction)
// console.log("execute", execute)
// console.log("PAGEEVENTS", PAGEEVENTS)

defineOptions({
  name: componentName,
})

// 动态解析自身组件
const selfComponent = resolveComponent(componentName)

const props = defineProps<{
  config: ComponentConnfig2_1
  parentConfig?: ComponentConnfig2_1
}>()

const conditionShow = computed(() => {
  if (props.config.conditions) {
    let shouldShow = true
    if (props.config.conditions.and) {
      shouldShow = props.config.conditions.and.every((condition: any) => {
        if (condition.type === "equal") {
          return pftDataCenter.value.getSharedData(condition.key) === condition.value
        }
        if (condition.type === "notEqual") {
          return pftDataCenter.value.getSharedData(condition.key) !== condition.value
        }
      })
    }
    if (!shouldShow) return false
    if (props.config.conditions.or) {
      shouldShow = props.config.conditions.or.some((condition: any) => {
        if (condition.type === "equal") {
          return pftDataCenter.value.getSharedData(condition.key) === condition.value
        }
        if (condition.type === "notEqual") {
          return pftDataCenter.value.getSharedData(condition.key) !== condition.value
        }
      })
    }
    return shouldShow
  }
  return true
})

const storeData = ref<any>(props.config.data || undefined)

const calcPosition = computed(() => {
  if (props.parentConfig && props.config.position) {
    const groupChildPosition = calcGroupOffset(toRaw(props.config.position))
    return groupChildPosition
  }

  // 非Group子组件，直接渲染position配置
  return props.config.position
})

// const ComponetClass = getComponent(props.config.component, props.config.kind);

const ComponetClass = ref<any>(null)

watch(
  () => props.config.component,
  async newVal => {
    if (newVal) {
      ComponetClass.value = await getComponent(newVal, props.config.kind)
    }
  },
  {
    immediate: true,
  },
)

const componentConfig = computed(() => {
  const { component, componentList, slot, dataset, processors, bind, ...config } = props.config
  const raw = toRaw(
    calcPropsBindData(
      {
        ...config,
        getComponent,
      },
      ComponetClass.value,
    ),
  )
  // 将绑定数据绑定到组件上
  if (bind && pftDataCenter) {
    Object.keys(bind).forEach(key => {
      raw[key] = pftDataCenter.value.getSharedData(bind[key])
    })
  }
  return raw
})

const { bind } = props.config

const componentEvents = ref<any>({})

// 处理绑定数据的更新事件
if (bind && pftDataCenter) {
  Object.keys(bind).forEach(key => {
    componentEvents.value[`update:${key}`] = (val: any) => {
      pftDataCenter.value.setSharedData(bind[key], val)
    }
  })
}

if (PAGEEVENTS) {
  watch(
    () => PAGEEVENTS.value,
    newVal => {
      if (newVal && newVal[props.config.id]) {
        // 事件绑定
        Object.keys(newVal[props.config.id]).forEach(event => {
          const { action } = newVal[props.config.id][event]
          switch (event) {
            case "onClick": {
              componentEvents.value["click"] = () => {
                if (!action) return
                excuteComponentAction(action)
              }
              break
            }
            case "onChange": {
              componentEvents.value["change"] = (val: any[]) => {
                if (!action) return
                const [key, value] = val
                console.log("onChange", val, key, value, action)
                if (!action[key]) return
                if (Array.isArray(action[key])) {
                  excuteComponentAction(
                    action[key].map(item => ({
                      ...item,
                      params: value,
                    })),
                  )
                } else {
                  excuteComponentAction({
                    ...action[key],
                    params: value,
                  })
                }
              }
              break
            }
            case "onToggle": {
              break
            }
          }
        })
      }
    },
    {
      immediate: true,
      deep: true,
    },
  )
}

const dataComponent = ref<any>(null)

let datasetId = props.config.id

const dataSource = computed(() => {
  const { dataset, id } = props.config
  if (!dataset) return undefined
  if (dataset.globalDataId) {
    datasetId = dataset.globalDataId
    return pftDataCenter.value?.get(dataset.globalDataId)
  }
  if (dataset.factoryId) {
    return pftDataCenter.value?.get(id)
  }
  return undefined
})

PageComponentsRef.value[props.config.id] = dataComponent

let dataSourceInit = false
// 如果组件不显示时，则不监听数据集变化
const silentDataset = () => {
  if (dataSourceInit) dataSource.value?.silent(props.config.id)
}
// 只有组件含有数据集及需要展示时才会监听数据集变化
const renderAndListenDataSet = () => {
  if (!dataSource.value) return silentDataset()
  if (!conditionShow.value) return silentDataset()
  if (!dataSourceInit) {
    dataSourceInit = true
    dataSource.value.active(props.config.id)
  }
  let { result } = dataSource.value
  // console.log('result', props.config.id, result, props.config);
  if (isRef(result)) {
    result = result.value
  }
  if (props.config.dataset.globalDataId && props.config.dataset.processors) {
    // 只有在使用全局数据源时才会执行全局数据源的处理器
    props.config.dataset.processors.forEach(process => {
      result = execute(datasetId, result, props.config.options, process)
    })
  }
  if (props.config.processors) {
    props.config.processors.forEach(process => {
      result = execute(datasetId, result, props.config.options, process)
    })
  }

  storeData.value = result
  if (dataComponent.value?.setData && result !== undefined) {
    dataComponent.value.setData(storeData.value)
  }
}

const debouncedRender = debounce(() => {
  renderAndListenDataSet()
}, 100)

watch(
  () => conditionShow.value,
  () => {
    debouncedRender()
  },
  { immediate: true },
)
if (props.config.dataset) {
  watch(
    () => dataSource.value,
    data => {
      debouncedRender()
    },
    { immediate: true, deep: true },
  )
  onUnmounted(() => {
    silentDataset()
  })
}

watch(
  () => dataComponent.value,
  newVal => {
    if (newVal?.setData && storeData.value !== undefined) {
      newVal.setData(storeData.value)
    }
  },
)
</script>
